import React from "react";
import type { InfoCardItem } from "./InfoCard";
import { formatSecondsToDuration } from "@/lib/utils";
import { formatEventName } from "@/lib/runs";
import { SiGithub } from "@icons-pack/react-simple-icons";
import { Clock, Zap, Calendar, CirclePlay } from "lucide-react";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";

/**
 * Creates a duration item with tooltip
 */
export const createDurationItem = (
  startTime?: string | null,
  endTime?: string | null,
  title = "Duration",
): InfoCardItem => {
  if (!startTime || !endTime) {
    return {
      title,
      icon: <Clock className="size-3" />,
      value: "Unknown",
    };
  }

  const runtimeSeconds =
    startTime && endTime
      ? Math.round((Date.parse(endTime) - Date.parse(startTime)) / 1000)
      : 0;

  return {
    title,
    icon: <Clock className="size-3" />,
    value: formatSecondsToDuration(runtimeSeconds),
  };
};

/**
 * Creates a trigger event item
 */
export const createTriggerItem = (event: string): InfoCardItem => ({
  title: "Trigger",
  icon: <Zap className="size-3" />,
  value: formatEventName(event),
});

/**
 * Creates a jobs summary item with status breakdown
 */
export const createJobsSummaryItem = (
  totalJobs: number,
  statusCounts: Record<string, number>,
  rerunCount = 0,
): InfoCardItem => {
  const parts = [`${totalJobs} total`];

  if (statusCounts.success > 0) {
    parts.push(`${statusCounts.success} passed`);
  }
  if (statusCounts.failure > 0) {
    parts.push(`${statusCounts.failure} failed`);
  }
  if (statusCounts.cancelled > 0) {
    parts.push(`${statusCounts.cancelled} cancelled`);
  }
  if (rerunCount > 0) {
    parts.push(`${rerunCount} rerun`);
  }

  return {
    title: "Jobs",
    icon: <CirclePlay className="size-3" />,
    value: parts.join(", "),
  };
};

/**
 * Creates a time ago item
 */
export const createTimeAgoItem = (
  timestamp?: string | null,
  label = "Started",
): InfoCardItem => {
  if (!timestamp) {
    return {
      title: label,
      icon: <Calendar className="size-3" />,
      value: "Unknown",
    };
  }

  const timeAgo = formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  const fullDate = new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  return {
    title: label,
    icon: <Calendar className="size-3" />,
    value: <span title={fullDate}>{timeAgo}</span>,
  };
};
